import React from 'react'
import { PageMetaDTO } from 'publication-types'

import { PageMeta } from 'common/routing/page-meta'
import { Props } from 'common/routing/page-resolver'

import {
    RouteInformation,
    ComponentServices,
    RouteServices,
    PageRouteInformation,
} from 'common/routing/page-definition'
import { getRouteInfoAdUnits } from 'component-library/advertising/get-route-ads'
import { AdState } from 'common/advertising/ad-state'
import { gptManager } from 'component-library/advertising/gpt/GptAdProvider/components/GptManager'
import { GptAdSlotDefinition } from 'common/advertising/gpt-ad-slot-definintion'
import { ContentWrapper } from 'component-library/compositions/ContentWrapper/ContentWrapper'
import { CountdownInactivity } from 'component-library/tools/CountdownInactivity'
import { isFeatureEnabled } from 'common/feature-togglings/features'

import { Heading } from 'component-library/typography/Heading/Heading'
import { Box } from 'component-library/compositions/Box/Box'
import { getAdState } from 'common/routing/get-ad-state'
import { PageRendererEvent } from 'common/events/page-events'
import { GptAdProvider } from 'component-library/advertising/gpt/GptAdProvider/GptAdProvider'
import { getDefaultConsentLevel } from 'common/data/consent/get-default-consent-level'
import { ConsentLevel } from 'common/data/consent/consent.redux'
import { hasConsentLevel } from 'common/data/consent/has-consent-level'
import { DataLayerEventName } from 'common/helpers/DataLayerEventName'

export function renderRouteInfo<Sections extends string>(
    routeServices: RouteServices,
    routeInfo: RouteInformation<Sections, any, any> | undefined,
    props: Props
) {
    let adState: AdState | undefined
    let unitsArray: GptAdSlotDefinition[] | undefined
    const storeCurrentState = routeServices.store.getState()
    const advertisingConsentGiven = hasConsentLevel(
        storeCurrentState.consent.consentLevel ||
            getDefaultConsentLevel(storeCurrentState.geoLocation),
        ConsentLevel.Advertising
    )
    const advertisingAndAnalyticsConsentHasBeenGiven = hasConsentLevel(
        storeCurrentState.consent.consentLevel ||
            getDefaultConsentLevel(storeCurrentState.geoLocation),
        ConsentLevel.Advertising | ConsentLevel.Analytics
    )
    const toggles = storeCurrentState.toggles
    const gptManagerInit = gptManager({
        enableGrapeshot:
            advertisingAndAnalyticsConsentHasBeenGiven && isFeatureEnabled(toggles, 'grapeshot'),
        enableMoatYieldIntelligence: isFeatureEnabled(toggles, 'moat-yield-intelligence'),
        logger: routeServices.log,
        advertisingConsentGiven,
    })

    if (routeInfo && routeInfo.kind === 'page') {
        const routeAds = getRouteInfoAdUnits(toggles, routeInfo.compositions)
        const pageAds = routeAds ? [...props.siteAds, ...routeAds] : props.siteAds
        ;({ unitsArray, adState } = getAdState(
            routeInfo.adTargeting,
            pageAds,
            props.location.pathname
        ))
        const componentServices: ComponentServices = {
            ...routeServices,
            adState,
            onEvent: props.onEvent,
            layout: props.layout,
            location: props.location,
        }
        // Pipe any server resolved metadata straight through to the page meta
        const serverResolutionMeta: PageMetaDTO =
            (routeServices.resolution &&
                routeServices.resolution.type === 'server' &&
                routeServices.resolution.resolution.type !== 'preview' &&
                routeServices.resolution.resolution.type !== 'profile' &&
                routeServices.resolution.resolution.meta) ||
            {}

        return (
            <GptAdProvider
                renderKey={adState.pageKey}
                slots={unitsArray}
                gptManager={gptManagerInit}
            >
                {props.renderPage(
                    <ContentWrapper
                        stretchColumns={props.stretchColumns}
                        hasBackgroundFill={routeInfo.headerHasBackgroundFill}
                    >
                        <PageMeta
                            serverMeta={serverResolutionMeta}
                            routeMeta={routeInfo}
                            location={props.location}
                            services={props.services}
                        />
                        {renderDisplayHeading(routeInfo)}
                        {routeInfo.refreshOnInactive &&
                            isFeatureEnabled(
                                props.services.store.getState().toggles,
                                'refresh-on-inactive',
                                true
                            ) && (
                                <CountdownInactivity
                                    timeToInactive={240}
                                    timeRequiredInactive={60}
                                    onEvent={e => coundownEventHandler(e, props)}
                                />
                            )}

                        <props.layout.CompositionsRenderer
                            services={componentServices}
                            compositions={routeInfo.compositions}
                        />
                    </ContentWrapper>,
                    adState,
                    routeInfo.pageType,
                    routeInfo.section,
                    {
                        title: routeInfo.heading,
                        ...routeInfo.additionalPageProperties,
                    }
                )}
            </GptAdProvider>
        )
    }

    // Required to utilize the section on loading routes so we dont get jank
    const sectionForNonPageRoute =
        (routeInfo && routeInfo.kind === 'loading' && routeInfo.section) || 'default'

    return (
        <GptAdProvider
            renderKey={adState ? adState.pageKey : undefined}
            slots={unitsArray || []}
            gptManager={gptManagerInit}
        >
            {props.renderPage(
                <ContentWrapper stretchColumns={props.stretchColumns} hasBackgroundFill={false}>
                    <Box verticalSpacing="md" hasTopSpacing horizontalSpacing="md">
                        <div style={{ minHeight: 400 }} />
                    </Box>
                </ContentWrapper>,
                adState,
                'unknown',
                sectionForNonPageRoute,
                {}
            )}
        </GptAdProvider>
    )
}

/**
 * Provides a header if the heading is meant to be displayed on the route otherwise renders nothing
 * @param {PageRouteInformation} routeInfo the page route information object
 * @memberof PageRenderer
 */
export function renderDisplayHeading(routeInfo: PageRouteInformation<any, any, any>) {
    return !routeInfo.hideHeading && routeInfo.heading !== undefined ? (
        <Box verticalSpacing="md" hasTopSpacing horizontalSpacing="md">
            <Heading text={routeInfo.heading} />
        </Box>
    ) : null
}

export function coundownEventHandler(event: PageRendererEvent, props: Props) {
    if (event.type === DataLayerEventName.countdownInactivityFinished) {
        if (props.refreshOverride) {
            props.refreshOverride()
        } else {
            // eslint-disable-next-line no-self-assign
            window.location.href = window.location.href
        }
        return
    }

    props.onEvent(event)
}
